/**
 * Open the connections this app needs before it needs them.
 *
 * Measured against QA: a request on a connection this browser has already opened takes
 * ~230ms; the same request on a new one takes ~970ms. The difference is DNS, TCP and TLS -
 * roughly 700ms - and it is paid separately for each host.
 *
 * Someone arriving from the portal pays it three times in a row, because each step only
 * starts the next host's handshake once the previous step has answered: this app's own API
 * for /v1/sso/redeem, then Firebase to sign in, then the API again for /v1/me.
 *
 * Starting those handshakes while the bundle is still parsing takes them off that chain.
 * Preconnect is a hint, costs one idle socket, and the browser drops it on its own if it
 * goes unused.
 */

/** Firebase's sign-in hosts. Fixed, and needed by everyone who signs in or resumes. */
const FIREBASE_ORIGINS = [
  'https://identitytoolkit.googleapis.com',
  'https://securetoken.googleapis.com',
];

function originOf(url: string): string | null {
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
}

function preconnect(origin: string): void {
  // Same-origin needs no hint: that connection is the one this page arrived on.
  if (origin === window.location.origin) return;

  for (const rel of ['dns-prefetch', 'preconnect']) {
    const link = document.createElement('link');
    link.rel = rel;
    link.href = origin;
    // Both the API and Firebase are read with credentials, and a preconnect only matches a
    // later request when its credentials mode matches too.
    if (rel === 'preconnect') link.crossOrigin = 'use-credentials';
    document.head.appendChild(link);
  }
}

let warmed = false;

/**
 * Start connecting to the API and to Firebase. Safe to call more than once: it does its
 * work on the first call and nothing afterwards.
 */
export function warmOrigins(options: { apiBaseUrl: string; firebase: boolean }): void {
  if (warmed || typeof document === 'undefined') return;
  warmed = true;

  const api = originOf(options.apiBaseUrl);
  if (api) preconnect(api);
  if (options.firebase) FIREBASE_ORIGINS.forEach(preconnect);
}
